import {useEffect, useMemo, useRef, useState} from 'react'
import {Offcanvas} from 'react-bootstrap'
import {Braces, Check, Clock3, Copy, Fingerprint, Search, UserRound} from 'lucide-react'
import {formatLogDate, initials, LogEntry, logTimestamp, parseLogData} from './logUtils'

type Props = {
  log: LogEntry | null
  onClose: () => void
  onInvestigate: (log: LogEntry) => void
  onFilterUser: (log: LogEntry) => void
}

const serialize = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2) ?? 'null'
  } catch {
    return String(value)
  }
}

const searchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')

const matchesField = (name: string, value: unknown, query: string): boolean =>
  !query || searchText(`${name} ${serialize(value)}`).includes(query)

const PayloadField = ({
  name,
  value,
  depth = 0,
  query = '',
}: {
  name: string
  value: unknown
  depth?: number
  query?: string
}) => {
  const [visibleCount, setVisibleCount] = useState(40)
  const isCollection = value !== null && typeof value === 'object'

  if (!matchesField(name, value, query)) return null
  if (!isCollection) {
    const display = value == null ? 'null' : value === '' ? '(texto vazio)' : String(value)
    return (
      <div className='logs-detail-field'>
        <dt className='logs-detail-key'>{name}</dt>
        <dd className={`logs-detail-value${value == null ? ' logs-detail-value--null' : ''}`}>
          {display}
        </dd>
      </div>
    )
  }

  const entries = Object.entries(value as Record<string, unknown>)
  const childQuery = query && searchText(name).includes(query) ? '' : query
  const matchingEntries = entries.filter(([key, child]) => matchesField(key, child, childQuery))
  const label = Array.isArray(value) ? 'itens' : 'campos'

  return (
    <div className='logs-detail-field logs-detail-field--collection'>
      <dt className='visually-hidden'>{name}</dt>
      <dd>
        <details className='logs-detail-tree' open={depth === 0 || Boolean(query)}>
          <summary>
            <span className='logs-detail-key'>{name}</span>
            <span className='logs-detail-count'>
              {entries.length} {label}
            </span>
          </summary>
          {entries.length === 0 ? (
            <p className='logs-detail-empty'>
              {Array.isArray(value) ? 'Lista vazia' : 'Objeto vazio'}
            </p>
          ) : depth >= 6 ? (
            <pre className='logs-detail-raw-code'>{serialize(value)}</pre>
          ) : (
            <>
              <dl>
                {matchingEntries.slice(0, visibleCount).map(([key, child]) => (
                  <PayloadField
                    key={key}
                    name={Array.isArray(value) ? `[${key}]` : key}
                    value={child}
                    depth={depth + 1}
                    query={childQuery}
                  />
                ))}
              </dl>
              {matchingEntries.length > visibleCount && (
                <button
                  type='button'
                  className='btn btn-sm btn-light logs-detail-show-more'
                  onClick={() => setVisibleCount((count) => count + 40)}
                >
                  Mostrar mais {Math.min(40, matchingEntries.length - visibleCount)} {label}
                </button>
              )}
            </>
          )}
        </details>
      </dd>
    </div>
  )
}

const LogDetailsDrawer = ({log, onClose, onInvestigate, onFilterUser}: Props) => {
  const [fieldSearch, setFieldSearch] = useState('')
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')
  const rawCodeRef = useRef<HTMLPreElement>(null)
  const payload = useMemo(() => parseLogData(log?.data), [log?.data])
  const rawJson = useMemo(() => serialize(payload), [payload])
  const query = searchText(fieldSearch.trim())
  const userName = log?.user || (log?.user_id != null ? `Usuário #${log.user_id}` : 'Sistema')
  const payloadEntries = useMemo(
    () =>
      payload !== null && typeof payload === 'object'
        ? Object.entries(payload as Record<string, unknown>)
        : [['Conteúdo', payload] as [string, unknown]],
    [payload]
  )
  const filteredEntries = payloadEntries.filter(([key, value]) => matchesField(key, value, query))

  useEffect(() => {
    setFieldSearch('')
    setCopyState('idle')
  }, [log?.id_log])

  const copyPayload = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable')
      await navigator.clipboard.writeText(rawJson)
      setCopyState('copied')
    } catch {
      setCopyState('error')
      const details = rawCodeRef.current?.closest('details')
      if (details) details.open = true
      rawCodeRef.current?.focus()
    }
  }

  return (
    <Offcanvas
      show={Boolean(log)}
      onHide={onClose}
      placement='end'
      className='logs-detail-drawer'
      aria-labelledby='logs-detail-title'
    >
      <Offcanvas.Header closeButton closeLabel='Fechar detalhes' className='logs-detail-header'>
        <div>
          <span className='logs-detail-eyebrow'>INSPEÇÃO DO EVENTO</span>
          <Offcanvas.Title id='logs-detail-title' className='logs-detail-title'>
            Detalhes do registro
          </Offcanvas.Title>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body className='logs-detail-body'>
        {log && (
          <>
            <section className='logs-detail-event' aria-label='Resumo do evento'>
              <span className='logs-detail-type'>{log.type || 'Sem tipo'}</span>
              <h3>{log.name || 'Evento sem descrição'}</h3>
              <dl className='logs-detail-meta'>
                <div>
                  <dt>
                    <Fingerprint size={15} aria-hidden='true' /> Identificador
                  </dt>
                  <dd>#{log.id_log}</dd>
                </div>
                <div>
                  <dt>
                    <Clock3 size={15} aria-hidden='true' /> Data e hora
                  </dt>
                  <dd>{formatLogDate(log.created_at)}</dd>
                </div>
                <div>
                  <dt>
                    <UserRound size={15} aria-hidden='true' /> Autor do evento
                  </dt>
                  <dd>
                    <span className='logs-detail-avatar' aria-hidden='true'>
                      {initials(userName)}
                    </span>
                    {userName}
                  </dd>
                </div>
                {log.user_id != null && (
                  <div>
                    <dt>ID do usuário</dt>
                    <dd>{log.user_id}</dd>
                  </div>
                )}
              </dl>
            </section>

            <div className='logs-detail-actions'>
              <button
                type='button'
                className='logs-detail-action logs-detail-action--primary'
                disabled={!Number.isFinite(logTimestamp(log.created_at))}
                onClick={() => {
                  onInvestigate(log)
                  onClose()
                }}
              >
                <Clock3 size={18} aria-hidden='true' />
                <span>
                  Investigar este momento<small>15 minutos antes e depois</small>
                </span>
              </button>
              <button
                type='button'
                className='logs-detail-action'
                onClick={() => {
                  onFilterUser(log)
                  onClose()
                }}
              >
                <UserRound size={18} aria-hidden='true' />
                <span>
                  Filtrar este autor<small>Ver outros eventos deste autor</small>
                </span>
              </button>
            </div>

            <section className='logs-detail-section' aria-labelledby='logs-detail-payload-title'>
              <div className='logs-detail-section-heading'>
                <div>
                  <Braces size={18} aria-hidden='true' />
                  <h3 id='logs-detail-payload-title'>Dados do evento</h3>
                </div>
                <button type='button' className='btn btn-sm btn-light' onClick={copyPayload}>
                  {copyState === 'copied' ? (
                    <Check size={14} aria-hidden='true' />
                  ) : (
                    <Copy size={14} aria-hidden='true' />
                  )}
                  {copyState === 'copied' ? 'Copiado' : 'Copiar JSON'}
                </button>
              </div>
              <div className='logs-detail-copy-status' role='status' aria-live='polite'>
                {copyState === 'copied' && 'JSON copiado para a área de transferência.'}
                {copyState === 'error' &&
                  'A cópia automática não está disponível. Selecione o JSON abaixo para copiar.'}
              </div>
              {payload == null ? (
                <p className='logs-detail-empty'>Este evento não possui dados adicionais.</p>
              ) : (
                <>
                  <label className='logs-detail-search'>
                    <Search size={16} aria-hidden='true' />
                    <span className='visually-hidden'>Buscar nos campos e valores do evento</span>
                    <input
                      type='search'
                      value={fieldSearch}
                      onChange={(event) => setFieldSearch(event.target.value)}
                      placeholder='Buscar campo ou valor...'
                    />
                  </label>
                  <dl className='logs-detail-payload' key={log.id_log}>
                    {filteredEntries.map(([key, value]) => (
                      <PayloadField
                        key={key}
                        name={Array.isArray(payload) ? `[${key}]` : key}
                        value={value}
                        query={query}
                      />
                    ))}
                  </dl>
                  {filteredEntries.length === 0 && (
                    <p className='logs-detail-empty'>
                      {query
                        ? 'Nenhum campo encontrado para esta busca.'
                        : 'Nenhum campo adicional foi registrado.'}
                    </p>
                  )}
                </>
              )}
              <details className='logs-detail-raw' key={`raw-${log.id_log}`}>
                <summary>
                  <Braces size={16} aria-hidden='true' /> Visualizar JSON bruto
                </summary>
                <pre
                  ref={rawCodeRef}
                  tabIndex={0}
                  className='logs-detail-raw-code'
                  aria-label='JSON do evento'
                >
                  {rawJson}
                </pre>
              </details>
            </section>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  )
}

export default LogDetailsDrawer
